import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Shield,
  RefreshCw,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  Lock,
  Unlock,
  AlertTriangle,
  Loader2,
  Eye,
} from 'lucide-react';
import secureStorage from '../utils/secureStorage';
import { API_BASE_URL } from '../config';

const API = `${API_BASE_URL}/api`;

const ACTION_STYLES = {
  create: 'bg-green-500/15 text-green-300 border-green-500/40',
  update: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  delete: 'bg-red-500/15 text-red-300 border-red-500/40',
  lock: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  unlock: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  phase_change: 'bg-purple-500/15 text-purple-300 border-purple-500/40',
  denied_locked: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40',
};

const ACTION_ICONS = {
  lock: <Lock className="h-3 w-3" />,
  unlock: <Unlock className="h-3 w-3" />,
  denied_locked: <AlertTriangle className="h-3 w-3" />,
};

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const ExerciseAuditTrail = ({ exerciseId, user }) => {
  const isSuperAdmin = user?.role === 'Super Admin';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({ action: '', actor_id: '', target_collection: '' });
  const [exportingCsv, setExportingCsv] = useState(false);

  const authHeaders = useCallback(() => {
    const token = secureStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(
    async ({ append = false } = {}) => {
      if (!isSuperAdmin || !exerciseId) return;
      setLoading(true);
      try {
        const params = { limit: 100 };
        if (append && nextCursor) params.before = nextCursor;
        if (filters.action) params.action = filters.action;
        if (filters.actor_id) params.actor_id = filters.actor_id;
        if (filters.target_collection) params.target_collection = filters.target_collection;
        const res = await axios.get(`${API}/exercise-builder/${exerciseId}/audit`, {
          headers: authHeaders(),
          params,
        });
        setTotal(res.data?.total || 0);
        setNextCursor(res.data?.next_cursor || null);
        setRows((prev) => (append ? [...prev, ...(res.data?.rows || [])] : res.data?.rows || []));
      } catch (err) {
        const detail = err?.response?.data?.detail || err.message;
        toast.error(`Audit load failed: ${detail}`);
      } finally {
        setLoading(false);
      }
    },
    [exerciseId, isSuperAdmin, filters, nextCursor, authHeaders]
  );

  useEffect(() => {
    // Fresh load whenever the filter set changes
    setNextCursor(null);
    setRows([]);
    load({ append: false });
  }, [exerciseId, filters.action, filters.actor_id, filters.target_collection]);

  const uniqueActors = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (r.actor_id && !map[r.actor_id]) {
        map[r.actor_id] = r.actor_name || r.actor_email || r.actor_id;
      }
    });
    return Object.entries(map);
  }, [rows]);

  const uniqueTargets = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.target_collection && s.add(r.target_collection));
    return Array.from(s).sort();
  }, [rows]);

  const downloadCsv = async () => {
    if (!isSuperAdmin || !exerciseId) return;
    setExportingCsv(true);
    try {
      const res = await axios.get(`${API}/exercise-builder/${exerciseId}/audit/export.csv`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_${exerciseId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Audit CSV downloaded');
    } catch (err) {
      toast.error(`CSV export failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setExportingCsv(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6 bg-gray-900 border border-red-500/40 rounded-lg text-center">
        <Shield className="h-8 w-8 mx-auto text-red-400 mb-2" />
        <h3 className="text-red-400 font-semibold">Super Admin only</h3>
        <p className="text-gray-400 text-sm mt-1">
          The audit trail is restricted to Super Admin accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="exercise-audit-trail">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-orange-500 font-semibold text-xl flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Audit Trail
          </h2>
          <p className="text-gray-500 text-xs">
            Every write, lock, unlock, delete or denied action on this exercise.
            {typeof total === 'number' && (
              <span className="ml-1 text-gray-400">({total} total events)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load({ append: false })}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 text-sm disabled:opacity-60"
            data-testid="audit-refresh-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            disabled={exportingCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-black font-semibold text-sm disabled:opacity-60"
            data-testid="audit-export-csv-btn"
          >
            {exportingCsv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 bg-gray-900/60 border border-gray-700 rounded-lg p-3">
        <Filter className="h-4 w-4 text-gray-400 mr-1" />
        <select
          value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-2 py-1.5"
          data-testid="audit-filter-action"
        >
          <option value="">All actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="lock">Lock</option>
          <option value="unlock">Unlock</option>
          <option value="phase_change">Phase change</option>
          <option value="denied_locked">Denied (locked)</option>
        </select>
        <select
          value={filters.actor_id}
          onChange={(e) => setFilters((f) => ({ ...f, actor_id: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-2 py-1.5"
          data-testid="audit-filter-actor"
        >
          <option value="">All actors</option>
          {uniqueActors.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={filters.target_collection}
          onChange={(e) => setFilters((f) => ({ ...f, target_collection: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-gray-100 text-sm rounded-md px-2 py-1.5"
          data-testid="audit-filter-target"
        >
          <option value="">All targets</option>
          {uniqueTargets.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {(filters.action || filters.actor_id || filters.target_collection) && (
          <button
            onClick={() => setFilters({ action: '', actor_id: '', target_collection: '' })}
            className="text-xs text-gray-400 hover:text-orange-400 ml-2"
            data-testid="audit-filter-clear"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-gray-900/60 border border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-gray-400 border-b border-gray-700">
              <tr>
                <th className="text-left px-3 py-2 w-8" />
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Target</th>
                <th className="text-left px-3 py-2">Path</th>
                <th className="text-right px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No audit events yet.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isOpen = !!expanded[r.id];
                const style = ACTION_STYLES[r.action] || 'bg-gray-500/15 text-gray-300 border-gray-500/40';
                const icon = ACTION_ICONS[r.action] || null;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      onClick={() => setExpanded((prev) => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className="cursor-pointer hover:bg-gray-800/50"
                      data-testid={`audit-row-${r.id}`}
                    >
                      <td className="px-3 py-2 text-gray-500">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                        {formatTs(r.timestamp)}
                      </td>
                      <td className="px-3 py-2 text-gray-100">
                        <div className="font-medium">{r.actor_name || r.actor_email || '—'}</div>
                        <div className="text-[10px] text-gray-500">{r.actor_role || ''}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${style}`}
                        >
                          {icon}
                          {r.action}
                          {r.denied && <span className="ml-1 text-red-300">(denied)</span>}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                        {r.target_collection || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 truncate max-w-[280px]" title={r.path}>
                        <span className="text-gray-600 mr-2">{r.method}</span>
                        {r.path}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-400 whitespace-nowrap">
                        {r.status_code}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-950/60">
                        <td />
                        <td colSpan={6} className="px-3 py-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-300">
                            <div>
                              <span className="text-gray-500">Actor ID:</span> {r.actor_id || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500">Actor IP:</span> {r.actor_ip || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500">Actor Email:</span>{' '}
                              {r.actor_email || '—'}
                            </div>
                            <div>
                              <span className="text-gray-500">Row ID:</span> {r.id}
                            </div>
                            {r.extra && Object.keys(r.extra).length > 0 && (
                              <div className="md:col-span-2">
                                <span className="text-gray-500">Extra:</span>
                                <pre className="mt-1 p-2 bg-black/40 rounded border border-gray-800 overflow-x-auto text-[11px] text-gray-200">
                                  {JSON.stringify(r.extra, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {nextCursor && (
          <div className="p-3 border-t border-gray-700 text-center">
            <button
              onClick={() => load({ append: true })}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50 text-sm disabled:opacity-60"
              data-testid="audit-load-more-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExerciseAuditTrail;
